import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import { toast } from 'react-toastify';
import OutletSearch from '../../SelectSearch/OutletSearch';
import { formatSalesValue } from '../../../utils/formatUtils';
import { gramVariations, kgVariations, literVariations, milliliterVariations } from '../../../data/unitVariations';
import { useLocation } from 'react-router-dom';
import { formatNumber, convertToDisplayUnit } from '../../../utils/quantityConversion';
import SubRecipeMainCard from './SubRecipeMainCard';
import { selectStyles } from '../../../utils/cssUtils';

const readonlyClass =
    'col-span-2 w-full px-2 py-1.5 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed';
const editableClass =
    'col-span-2 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500';
const gridLabelClass = 'col-span-1 font-medium text-gray-700 self-center text-sm';

// ─── Component ────────────────────────────────────────────────────────────────
const SubRecipeCost = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem('token');

    const [unitDefinitions, setUnitDefinitions] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [costItems, setCostItems] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [draftCostItem, setDraftCostItem] = useState(null);

    const recipeNameRef = useRef(null);
    const recipeUomRef = useRef(null);
    const recipeUnitRef = useRef(null);
    const ingredientSelectRef = useRef(null);
    const quantityRef = useRef(null);
    const uomSelectRef = useRef(null);

    const location = useLocation();
    const incomingSubRecipe = location.state?.receivedData;

    const [formData, setFormData] = useState({
        name: '',
        uom: '',
        unit: '',
        costItem: '',
        rate: '',
        itemUnit: '',
        itemUom: '',
        quantity: '',
        new_uom: '',
    });

    // ── Normalise UOM string ────────────────────────────────────────────────
    const normalizeUom = useCallback((uom) => {
        if (!uom) return '';
        const lowerUom = uom.toLowerCase().trim();
        if (kgVariations.includes(lowerUom)) return 'kg';
        if (gramVariations.includes(lowerUom)) return 'g';
        if (literVariations.includes(lowerUom)) return 'ltr';
        if (milliliterVariations.includes(lowerUom)) return 'ml';
        if (lowerUom === 'grm') return 'g';
        if (lowerUom === 'l') return 'ltr';
        return lowerUom;
    }, []);

    // ── Fetch unit definitions ──────────────────────────────────────────────
    const fetchUnitDefinitions = useCallback(async () => {
        if (!token || !selectedOutlet) return;
        try {
            const response = await axios.get(`${baseUrl}/unit-definitions`, {
                params: { token, outlet: selectedOutlet },
            });
            setUnitDefinitions(response.data);
        } catch (error) {
            console.error('Error fetching unit definitions:', error);
            toast.error('Failed to fetch unit definitions');
        }
    }, [token, selectedOutlet, baseUrl]);

    useEffect(() => {
        fetchUnitDefinitions();
    }, [fetchUnitDefinitions]);

    // ── Load incoming sub-recipe for editing ───────────────────────────────
    useEffect(() => {
        if (!incomingSubRecipe) return;

        setFormData((prev) => ({
            ...prev,
            name: incomingSubRecipe.name || '',
            uom: normalizeUom(incomingSubRecipe.uom),
            unit: incomingSubRecipe.unit || '',
        }));

        const mappedItems = incomingSubRecipe.items.map((item) => {
            const normalizedItemUom = normalizeUom(item.uom);
            const normalizedNewUom = normalizeUom(item.new_uom || item.uom);

            // Mirror MenuCostPrice: convert base quantity back to display unit
            const displayQty = convertToDisplayUnit(
                item.quantity,
                normalizedItemUom,
                normalizedNewUom,
                item.unit
            );

            return {
                name: item.name,
                rate: item.rate,
                itemUnit: item.unit,
                itemUom: normalizedItemUom,
                new_uom: normalizedNewUom,
                quantity: displayQty,
                cost: item.cost,
            };
        });

        setCostItems(mappedItems);
    }, [incomingSubRecipe, normalizeUom]);

    // ── Auto-focus recipe name on load ────────────────────────────────────
    useEffect(() => {
        if (recipeNameRef.current) recipeNameRef.current.focus();
    }, []);

    // ── Input handlers ─────────────────────────────────────────────────────
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if ((name === 'unit' || name === 'quantity') && value && isNaN(value)) return;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCostItemSelect = (selectedOption) => {
        if (!selectedOption) {
            setDraftCostItem(null);
            setFormData((prev) => ({
                ...prev,
                costItem: '',
                rate: '',
                itemUnit: '',
                itemUom: '',
                quantity: '',
                new_uom: '',
            }));
            return;
        }

        const selectedItem = unitDefinitions.find((item) => item.name === selectedOption.value);
        if (!selectedItem) return;

        const normalizedUom = normalizeUom(selectedItem.uom);
        const showConv = ['kg', 'g', 'ltr', 'ml'].includes(normalizedUom);
        const defaultNewUom = showConv
            ? ['kg', 'g'].includes(normalizedUom) ? 'g' : 'ml'
            : normalizedUom;

        setFormData((prev) => ({
            ...prev,
            costItem: selectedItem.name,
            rate: selectedItem.rate || '',
            itemUnit: selectedItem.unit || '',
            itemUom: normalizedUom,
            new_uom: defaultNewUom,
            quantity: '',
        }));

        // Show a draft row immediately (mirrors MenuCostPrice behaviour)
        setDraftCostItem({
            name: selectedItem.name,
            rate: selectedItem.rate || '',
            itemUnit: selectedItem.unit || '',
            itemUom: normalizedUom,
            new_uom: defaultNewUom,
            quantity: '',
            cost: 0,
            isDraft: true,
        });

        setTimeout(() => quantityRef.current?.focus(), 100);
    };

    const handleUomSelectChange = (selectedOption) => {
        const new_uom = selectedOption ? selectedOption.value : '';
        setFormData((prev) => {
            // Recalculate draft cost when UOM changes
            if (draftCostItem) {
                const cost =
                    prev.quantity && prev.rate && prev.itemUnit
                        ? calculateCost(prev.quantity, prev.itemUnit, prev.rate, prev.itemUom, new_uom)
                        : 0;
                setDraftCostItem((d) => ({ ...d, new_uom, cost: isNaN(cost) ? 0 : cost }));
            }
            return { ...prev, new_uom };
        });
    };

    // ── Cost calculation (same logic as original) ──────────────────────────
    const calculateCost = (quantity, itemUnit, rate, itemUom, new_uom) => {
        let unit = parseFloat(itemUnit) || 0;
        const qty = parseFloat(quantity);
        const r = parseFloat(rate) || 0;

        if (new_uom === 'g' && itemUom === 'kg') unit *= 1000;
        else if (new_uom === 'kg' && itemUom === 'g') unit /= 1000;
        else if (new_uom === 'ml' && itemUom === 'ltr') unit *= 1000;
        else if (new_uom === 'ltr' && itemUom === 'ml') unit /= 1000;

        return unit > 0 ? (qty / unit) * r : 0;
    };

    // Update draft cost as quantity is typed
    const handleQuantityChange = (e) => {
        const value = e.target.value;
        if (value && isNaN(value)) return;

        setFormData((prev) => {
            if (draftCostItem) {
                const cost =
                    value && prev.rate && prev.itemUnit
                        ? calculateCost(value, prev.itemUnit, prev.rate, prev.itemUom, prev.new_uom || prev.itemUom)
                        : 0;
                setDraftCostItem((d) => ({
                    ...d,
                    quantity: value,
                    cost: isNaN(cost) ? 0 : cost,
                }));
            }
            return { ...prev, quantity: value };
        });
    };

    // ── Add / update cost item ─────────────────────────────────────────────
    const handleAddCostItem = (e) => {
        e?.preventDefault();

        if (
            !formData.name ||
            !formData.costItem ||
            !formData.quantity ||
            (['kg', 'ltr'].includes(formData.itemUom) && !formData.new_uom)
        ) {
            toast.info('Please fill all required fields');
            return;
        }

        const quantity = parseFloat(formData.quantity);
        if (isNaN(quantity) || quantity <= 0) {
            toast.error('Please enter a valid quantity');
            return;
        }

        const exists = costItems.some(
            (item, idx) => item.name === formData.costItem && idx !== editingIndex
        );
        if (exists && editingIndex === null) {
            toast.info('This ingredient already exists');
            return;
        }

        const cost = calculateCost(
            quantity,
            formData.itemUnit,
            formData.rate,
            formData.itemUom,
            formData.new_uom || formData.itemUom
        );

        const newItem = {
            name: formData.costItem,
            rate: formData.rate,
            uom: formData.itemUom,
            new_uom: formData.new_uom || formData.itemUom,
            quantity: quantity.toString(),
            cost,
            unit: formData.unit,
            itemUnit: formData.itemUnit,
            itemUom: formData.itemUom,
            isDraft: false,
        };

        if (editingIndex !== null) {
            const updated = [...costItems];
            updated[editingIndex] = newItem;
            setCostItems(updated);
            setEditingIndex(null);
        } else {
            setCostItems((prev) => [...prev.filter((i) => !i.isDraft), newItem]);
        }

        setDraftCostItem(null);
        setFormData((prev) => ({
            ...prev,
            costItem: '',
            rate: '',
            itemUnit: '',
            itemUom: '',
            quantity: '',
            new_uom: '',
        }));

        setTimeout(() => ingredientSelectRef.current?.focus(), 100);
    };

    // ── Edit / cancel / delete ─────────────────────────────────────────────
    const handleEdit = (index) => {
        if (index < 0 || index >= costItems.length) return;
        setEditingIndex(index);

        const item = costItems[index];

        const liveDef = unitDefinitions.find((u) => u.name === item.name);

        let resolvedItemUom = item.itemUom;
        let resolvedItemUnit = item.itemUnit;
        let resolvedRate = item.rate;

        if (liveDef) {
            resolvedItemUom = normalizeUom(liveDef.uom);
            resolvedItemUnit = liveDef.unit;
            resolvedRate = liveDef.rate;
        }

        const hasConversion = ['kg', 'g', 'ltr', 'ml'].includes(resolvedItemUom);

        let resolvedNewUom;
        if (hasConversion) {
            const isKgFamily = kgVariations.includes(resolvedItemUom) || gramVariations.includes(resolvedItemUom);
            const savedNewUom = item.new_uom?.toLowerCase();
            const savedStillValid = isKgFamily
                ? ['g', 'gm', 'kg'].includes(savedNewUom)
                : ['ml', 'ltr', 'l'].includes(savedNewUom);

            resolvedNewUom = savedStillValid ? item.new_uom : (isKgFamily ? 'g' : 'ml');
        } else {
            resolvedNewUom = resolvedItemUom;
        }

        setFormData((prev) => ({
            ...prev,
            costItem: item.name,
            rate: resolvedRate,
            itemUnit: resolvedItemUnit,
            itemUom: resolvedItemUom,
            new_uom: resolvedNewUom,
            quantity: item.quantity.toString(),
        }));

        setDraftCostItem({
            ...item,
            rate: resolvedRate,
            itemUnit: resolvedItemUnit,
            itemUom: resolvedItemUom,
            new_uom: resolvedNewUom,
            isDraft: true,
            _editingIndex: index,
        });

        setTimeout(() => quantityRef.current?.focus(), 100);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setDraftCostItem(null);
        setFormData((prev) => ({
            ...prev,
            costItem: '',
            rate: '',
            itemUnit: '',
            itemUom: '',
            quantity: '',
            new_uom: '',
        }));
        setTimeout(() => ingredientSelectRef.current?.focus(), 100);
    };

    const handleDelete = (index) => {
        if (index < 0 || index >= costItems.length) return;
        setCostItems(costItems.filter((_, i) => i !== index));
        if (editingIndex === index) handleCancelEdit();
        else if (editingIndex > index) setEditingIndex((prev) => prev - 1);
    };

    // ── Keyboard navigation ────────────────────────────────────────────────
    const handleQuantityKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (showConversionOptions && uomOptions.length > 0) {
            uomSelectRef.current?.focus();
        } else {
            handleAddCostItem(e);
        }
    };

    const handleUomKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        handleAddCostItem(e);
    };

    // ── Derived values ─────────────────────────────────────────────────────
    const itemOptions = useMemo(
        () => unitDefinitions.map((item) => ({ value: item.name, label: item.name })),
        [unitDefinitions]
    );

    const showConversionOptions = useMemo(
        () => ['kg', 'g', 'ltr', 'ml'].includes(formData.itemUom),
        [formData.itemUom]
    );

    const getUomOptions = useCallback((uom) => {
        const lowerUom = uom?.toLowerCase();
        if (kgVariations.includes(lowerUom) || gramVariations.includes(lowerUom)) {
            return { options: [{ value: 'g', label: 'g' }, { value: 'kg', label: 'kg' }], defaultOption: 'g' };
        }
        if (literVariations.includes(lowerUom) || milliliterVariations.includes(lowerUom)) {
            return { options: [{ value: 'ml', label: 'ml' }, { value: 'ltr', label: 'ltr' }], defaultOption: 'ml' };
        }
        return { options: [], defaultOption: null };
    }, []);

    const { options: uomOptions, defaultOption: defaultUom } = useMemo(
        () => getUomOptions(formData.itemUom),
        [formData.itemUom, getUomOptions]
    );

    useEffect(() => {
        if (uomOptions.length > 0 && !formData.new_uom && formData.itemUom) {
            setFormData((prev) => ({ ...prev, new_uom: defaultUom }));
        }
    }, [uomOptions, formData.new_uom, formData.itemUom, defaultUom]);

    // ── Table display rows (with live draft, mirrors MenuCostTable) ────────
    const displayCostItems = useMemo(() => {
        const confirmed = costItems.filter((i) => !i.isDraft);
        if (!draftCostItem) return confirmed;
        if (draftCostItem._editingIndex !== undefined) {
            return confirmed.map((item, i) =>
                i === draftCostItem._editingIndex ? draftCostItem : item
            );
        }
        return [...confirmed, draftCostItem];
    }, [costItems, draftCostItem]);

    const totalCost = useMemo(
        () => costItems.filter((i) => !i.isDraft).reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0),
        [costItems]
    );

    // ── Base-quantity conversion for save/update payloads ─────────────────
    const convertQuantityToBaseUnit = (quantity, uom, new_uom, itemUnit) => {
        let converted = parseFloat(quantity);
        if (new_uom && uom !== new_uom) {
            if (kgVariations.includes(uom) && gramVariations.includes(new_uom)) converted /= 1000;
            else if (gramVariations.includes(uom) && kgVariations.includes(new_uom)) converted *= 1000;
            else if (literVariations.includes(uom) && milliliterVariations.includes(new_uom)) converted /= 1000;
            else if (milliliterVariations.includes(uom) && literVariations.includes(new_uom)) converted *= 1000;
        }
        const unitValue = parseFloat(itemUnit) || 1;
        return converted / unitValue;
    };

    // ── Save ───────────────────────────────────────────────────────────────
    const showSaveConfirmation = useCallback(() => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this sub-recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                const payload = {
                                    token,
                                    name: formData.name,
                                    outlet: selectedOutlet,
                                    uom: formData.uom,
                                    unit: formData.unit,
                                    costprice: parseFloat(totalCost),
                                    items: costItems.map((item) => ({
                                        name: item.name,
                                        rate: parseFloat(item.rate) || 0,
                                        uom: item.itemUom,
                                        unit: item.itemUnit,
                                        new_uom: item.new_uom,
                                        quantity: convertQuantityToBaseUnit(
                                            item.quantity,
                                            item.itemUom,
                                            item.new_uom,
                                            item.itemUnit
                                        ).toString(),
                                        cost: parseFloat(item.cost) || 0,
                                    })),
                                };
                                await axios.post(`${baseUrl}/sub-recipe`, payload);
                                toast.success('Sub-recipe saved successfully!');
                                setCostItems([]);
                                setFormData({ name: '', uom: '', unit: '', costItem: '', rate: '', itemUnit: '', itemUom: '', quantity: '', new_uom: '' });
                            } catch (error) {
                                console.error('Error saving sub-recipe:', error);
                                toast.error('Failed to save sub-recipe');
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            { autoClose: false, closeButton: false, toastId: 'save-subrecipe-confirmation' }
        );
    }, [baseUrl, costItems, formData.name, formData.uom, formData.unit, selectedOutlet, token, totalCost]);

    // ── Update ─────────────────────────────────────────────────────────────
    const handleUpdate = () => {
        if (!incomingSubRecipe?.id) { toast.error('No recipe selected for update'); return; }
        if (costItems.filter((i) => !i.isDraft).length === 0) { toast.error('Please add at least one ingredient'); return; }
        if (!formData.name || !formData.uom || !formData.unit) { toast.error('Please fill all sub-recipe details'); return; }

        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to update this sub-recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss('update-subrecipe-confirmation');
                            try {
                                await axios.put(`${baseUrl}/sub-recipe/${incomingSubRecipe.id}`, {
                                    token,
                                    outlet: selectedOutlet,
                                    name: formData.name,
                                    uom: formData.uom,
                                    unit: formData.unit,
                                    costprice: totalCost,
                                    items: costItems.filter((i) => !i.isDraft).map((item) => ({
                                        name: item.name,
                                        rate: parseFloat(item.rate) || 0,
                                        uom: item.itemUom,
                                        unit: item.itemUnit,
                                        new_uom: item.new_uom,
                                        quantity: convertQuantityToBaseUnit(
                                            item.quantity,
                                            item.itemUom,
                                            item.new_uom,
                                            item.itemUnit
                                        ).toString(),
                                        cost: parseFloat(item.cost) || 0,
                                    })),
                                });
                                toast.success('Sub-recipe updated successfully!');
                            } catch (error) {
                                console.error('Error updating sub-recipe:', error);
                                toast.error('Failed to update sub-recipe');
                            }
                        }}
                    >
                        Update
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('update-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            { autoClose: false, closeButton: false, toastId: 'update-subrecipe-confirmation' }
        );
    };

    const handleSave = () => {
        if (costItems.filter((i) => !i.isDraft).length === 0) { toast.error('Please add at least one ingredient'); return; }
        if (!formData.name || !formData.uom || !formData.unit) { toast.error('Please fill all sub-recipe details'); return; }
        showSaveConfirmation();
    };

    const isEditMode = !!incomingSubRecipe?.id;

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />

            <div className="flex-grow bg-gray-50">
                <div className="max-w-6xl mx-auto py-3 text-sm">

                    {/* ── Header row ── */}
                    <div className="flex justify-between mb-3 items-center">
                        <div className="flex-1 flex justify-start">
                            <div className="text-lg font-semibold">Sub-Recipe Cost Calculation ---</div>
                        </div>
                        <div className="flex-1 flex justify-center">
                            <OutletSearch
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                                token={token}
                            />
                        </div>
                        <div className="flex-1 flex justify-end" />
                    </div>

                    {/* ── Main card ── */}
                    <div className="bg-white p-4 rounded-lg text-sm">
                        <SubRecipeMainCard
                            // Classes
                            gridLabelClass={gridLabelClass}
                            editableClass={editableClass}
                            readonlyClass={readonlyClass}
                            selectStyles={selectStyles}

                            // Refs
                            recipeNameRef={recipeNameRef}
                            recipeUomRef={recipeUomRef}
                            recipeUnitRef={recipeUnitRef}
                            ingredientSelectRef={ingredientSelectRef}
                            quantityRef={quantityRef}
                            uomSelectRef={uomSelectRef}

                            // State
                            formData={formData}
                            itemOptions={itemOptions}
                            uomOptions={uomOptions}
                            displayCostItems={displayCostItems}
                            editingIndex={editingIndex}
                            showConversionOptions={showConversionOptions}

                            // Handlers
                            handleInputChange={handleInputChange}
                            handleCostItemSelect={handleCostItemSelect}
                            handleQuantityChange={handleQuantityChange}
                            handleQuantityKeyDown={handleQuantityKeyDown}
                            handleUomSelectChange={handleUomSelectChange}
                            handleUomKeyDown={handleUomKeyDown}
                            handleAddCostItem={handleAddCostItem}
                            handleCancelEdit={handleCancelEdit}
                            handleEdit={handleEdit}
                            handleDelete={handleDelete}

                            // Utils
                            formatSalesValue={formatSalesValue}
                            formatNumber={formatNumber}
                        />

                        {/* ── Footer bar: totals + save ── */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex gap-2.5 items-center">
                                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Total Cost</label>
                                <input
                                    type="number"
                                    value={totalCost.toFixed(2)}
                                    readOnly
                                    className="h-[36px] px-2 w-44 border border-gray-300 rounded-md text-sm font-medium text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex gap-2.5 items-center">
                                <label className="text-sm font-medium text-gray-700">Unit</label>
                                <input
                                    type="number"
                                    name="unit"
                                    value={formData.unit}
                                    onChange={handleInputChange}
                                    className="h-[36px] px-2 w-32 border border-gray-300 rounded-md text-sm font-medium text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-500">{formData.uom}</span>
                            </div>

                            <div className="flex gap-2.5 items-center">
                                <label className="text-sm font-medium text-gray-700">Cost / Unit</label>
                                <input
                                    type="text"
                                    value={
                                        formData.unit && parseFloat(formData.unit) > 0
                                            ? formatSalesValue(totalCost / parseFloat(formData.unit))
                                            : '—'
                                    }
                                    readOnly
                                    className="h-[36px] px-2 w-44 border border-gray-200 bg-gray-100 rounded-md text-sm font-semibold text-center text-gray-700"
                                />
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={isEditMode ? handleUpdate : handleSave}
                                    disabled={costItems.filter((i) => !i.isDraft).length === 0}
                                    className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                                >
                                    {isEditMode ? 'Update Sub-Recipe' : 'Save Sub-Recipe'}
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <Footer />
        </div>
    );
};

export default SubRecipeCost;